import React, { useEffect, useMemo, useState } from "react";

import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { connect } from "react-redux";

import { Block, Spinner } from "components";
import Modal from "components/Modal/Modal";
import PromoCodesForm from "components/PromoCodesForm/PromoCodesForm";
import { actions as promoManagerActions } from "coupon-common/src/modules/admin/promoManager";

const PromoCodesList = ({ dispatch, promoCodes, fetching, onUpdate, searchQuery, loading }) => {
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 🔥 Fetch promo codes
  useEffect(() => {
    dispatch(promoManagerActions.getPromoCodes());
  }, [dispatch]);

  const filteredData = useMemo(() => {
    if (!searchQuery) return promoCodes;

    const q = searchQuery.toLowerCase();

    return promoCodes?.filter((promo) =>
      promo.code?.toLowerCase().includes(q) ||
      String(promo.discount_amount || "").includes(q) ||
      String(promo.discount_percent || "").includes(q) ||
      String(promo.max_uses || "").includes(q)
      // String(promo. || "").includes(q)
    );
  }, [promoCodes, searchQuery]);

  const paginationOptions = {
    sizePerPage: 10,
    totalSize: filteredData?.length || 0,
    showTotal: true,
    sizePerPageList: [10, 25, 50],
  };

  const handleEdit = (promo) => {
    setSelectedPromo(promo);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setSelectedPromo(null);
    setIsModalOpen(false);
  };

  // 🔥 Update
  const handleUpdate = async (updatedValues) => {
    if (!selectedPromo) return;

    await onUpdate(selectedPromo.id, updatedValues);

    handleClose();
  };

  // 🔥 Columns
  const columns = [
    {
      dataField: "code",
      text: "Code",
      sort: true,
    },
    {
      dataField: "applies_to",
      text: "Applies To",
      formatter: (_, row) => {
        if (row.mem_id) {
          return row.mem_id
        }
        if (row.product_id) {
          return `Product: ${row.product_id}`;
        }
      },
    },
    {
      dataField: "discount",
      text: "Discount",
      formatter: (_, row) =>
        row.discount_percent
          ? `${row.discount_percent}%`
          : `$${row.discount_amount}`,
    },
    {
      dataField: "max_uses",
      text: "Max Uses",
      formatter: (cell) => cell || "Unlimited",
    },
    {
      dataField: "times_used",
      text: "Times Used",
      formatter: (cell) => cell || 0,
    },
    {
      dataField: "valid_until",
      text: "Valid Until",
      formatter: (cell) =>
        cell ? new Date(cell).toLocaleDateString() : "No Expiry",
    },
    {
      dataField: "is_active",
      text: "Status",
      formatter: (cell) =>
        cell ? (
          <span style={{ color: "green" }}>Active</span>
        ) : (
          <span style={{ color: "red" }}>Inactive</span>
        ),
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => (
        <button
          className="btn-primary"
          style={{ padding: "5px 10px", cursor: "pointer" }}
          onClick={() => handleEdit(row)}
        >
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className="col-md-12">
      <Block>
        <h4 style={{ marginBottom: "15px" }}>Promo Codes List</h4>

        {fetching ? (
          <Spinner show />
        ) : (
          <div className="table-container overflow-auto">
            <BootstrapTable
              keyField="id"
              data={filteredData || []}
              columns={columns}
              bootstrap4
              bordered={false}
              striped
              pagination={paginationFactory(paginationOptions)}
              classes="table-borderless custom-react-bootstrap-table"
            />
          </div>
        )}
      </Block>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={handleClose}
        size="lg"
      >
        {selectedPromo && (
          <PromoCodesForm
            mode="edit"
            initialValues={selectedPromo}
            onSubmit={handleUpdate}
            loading={loading}
          />
        )}
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  promoCodes: state.admin?.promoManager?.list?.data || [],
  fetching: state.admin?.promoManager?.list?.fetching,
});

export default connect(mapStateToProps)(PromoCodesList);